import { nullish } from 'Lib/type-sanity';
import { randIntBetween } from 'Lib/number-util';

/**
 * Returns a new array of random length between `min_length` and `max_length`, filled with
 * values produced by `constructor`.
 *
 * By default, `constructor` returns random integers.
 *
 * @param { { constructor: Function<unknown>, min_length: number, max_length: number } } options
 *
 * @returns unknown[]
 */
// eslint-disable-next-line snakecasejs/snakecasejs
export const makeRandArr = (options = {}) => {
  options = {
    constructor: () => randIntBetween(Number.MIN_SAFE_INTEGER, Number.MAX_SAFE_INTEGER),
    min_length: 1,
    max_length: 1000,
    ...options,
  };
  if (options.min_length >= options.max_length) {
    options.min_length = options.max_length - 1;
  }
  return Array.from({
    length: randIntBetween(options.min_length, options.max_length),
  }, options.constructor);
};

/**
 * Generates a numeric sequence in the range [`initial` .. `end`].
 * Either a `step` or `count` may be defined.
 * - `step` will explicitly define the seperation between generated numbers
 * - `count` will generate `step` internally to ensure the sequence contains `count` steps (`.length` will be `count + 1`)
 *
 * If both are provided, `count` will be discarded.
 *
 * @param {{ initial: number, end: number, step: number?, count: number? }} options
 *
 * @returns { number[] }
 */
export const sequence = (options) => {
  for (const [key, val] of Object.entries(options)) {
    if (!nullish(val)) {
      options[key] = parseFloat(val);
    }
  }
  // discard `count` if `step` provided:
  if (!nullish(options.count) && !nullish(options.step)) {
    options.count = null;
  }
  // calculate `step` based on `count`:
  if (!nullish(options.count) && nullish(options.step)) {
    options.step = (parseFloat(options.end) - options.initial) / options.count;
  }
  // return array of length `count`, or (end - initial) / `step` + 1,
  // filled with numeric sequence
  return Array.from({
    length: nullish(options.count) ? (options.end - options.initial) / options.step + 1 : options.count + 1,
  }, (v, k) => options.initial + (k * options.step));
};

/**
 * Checks to see if the supplied array is a linear sequence
 * of numbers. Elements are compared with the supplied precision level.
 *
 * @param { number[] } arr
 * @param { number } precision
 *
 * @returns { boolean }
 */
// eslint-disable-next-line snakecasejs/snakecasejs
export const isLinear = (arr, precision = 6) => {
  if (arr.length <= 1) return false;

  const differences = [];
  for (let i = 1; i < arr.length; ++i) {
    differences.push(Math.abs(arr[i] - arr[i - 1]));
  }
  // all differences must be equal
  return differences.every(diff => diff.toPrecision(precision) === differences[0].toPrecision(precision));
};

/**
 * Checks to see if all members of `r` are included in `l`,
 * i.e that `r` is a *subset* of `l`
 *
 * @param { unknown[] } l
 *
 * @returns { (...r: unknown[]) => boolean }
 */
// eslint-disable-next-line snakecasejs/snakecasejs
export const subSet = (...l) => (...r) => r.every(val => l.includes(val));

/**
 * 'Chunks' an array, meaning the array is split into smaller arrays of a given size.
 *
 * @param { unknown[] } arr The array to chunk
 * @param { number } size The size of chunks produced
 * @returns { unknown[][] }
 */
export const chunked = (arr, size) => Array.from({
  length: Math.ceil(arr.length / size),
}, (v, i) =>
  arr.slice(i * size, i * size + size),
);
